/**
 * Clock offset utility for correcting system clock drift.
 *
 * When a device's system clock is off (even by a few minutes), the player's
 * live pipeline breaks because it requests data for wrong time ranges.
 * This module measures the offset between the local clock and the server clock,
 * then provides a correctedNow() function that compensates for the drift.
 */

let clockOffset = 0;
const CLOCK_OFFSET_WARN_THRESHOLD_MS = 30_000; // 30 seconds

/**
 * Calibrate the local clock against the server by measuring the offset
 * from the HTTP Date response header.
 *
 * Uses a simple NTP-like approach: measures round-trip time (RTT) and
 * estimates that the server timestamp corresponds to the midpoint of the request.
 */
export async function calibrateClock(serverUrl: string): Promise<void> {
  try {
    const before = Date.now();
    const res = await fetch(serverUrl, { method: 'HEAD' });
    const after = Date.now();
    const dateHeader = res.headers.get('Date');
    if (dateHeader == null) return;

    const serverTime = new Date(dateHeader).getTime();
    if (Number.isNaN(serverTime)) return;

    const rtt = after - before;
    clockOffset = serverTime + rtt / 2 - after;

    if (Math.abs(clockOffset) > CLOCK_OFFSET_WARN_THRESHOLD_MS) {
      console.warn(
        `[clock] Device clock is off by ${Math.round(clockOffset / 1000)}s from server time.`,
      );
    }
  } catch (err) {
    console.warn('[clock] Failed to calibrate clock:', err);
  }
}

/**
 * Returns the current time corrected for the measured clock offset.
 * If calibration hasn't been performed or failed, returns Date.now() as-is.
 */
export function correctedNow(): number {
  return Date.now() + clockOffset;
}

/**
 * Returns the measured clock offset in milliseconds.
 * Positive means local clock is behind server; negative means ahead.
 */
export function getClockOffset(): number {
  return clockOffset;
}

/**
 * Returns true if the clock offset exceeds the warning threshold.
 */
export function isClockOffsetSignificant(): boolean {
  return Math.abs(clockOffset) > CLOCK_OFFSET_WARN_THRESHOLD_MS;
}

/**
 * Resets the clock offset (mainly useful for testing).
 */
export function resetClockOffset(): void {
  clockOffset = 0;
}
